import { useEffect } from 'react';
import useSeoSettings from '../hooks/useSeoSettings';

/**
 * JsonLd — emits a single Schema.org JSON-LD block describing the page.
 * Usage:
 *
 *   <JsonLd type="LocalBusiness" />            // home: pulls everything from settings
 *   <JsonLd type="Service" data={{ ... }} />   // a specific service
 *   <JsonLd type="BookingAction" data={{ ... }} /> // booking page
 *   <JsonLd raw={{ "@context": ..., "@type": ..., ... }} /> // arbitrary
 *
 * Behaviour:
 *   - On mount we add a <script type="application/ld+json"> tag with a
 *     deterministic id derived from `id` prop (defaults to "jsonld-<type>").
 *   - On unmount we remove the tag so per-page schema doesn't leak across
 *     SPA navigations (this is the one cleanup that matters for JSON-LD
 *     because Google reads ALL ld+json scripts in the DOM and combining
 *     "Home page LocalBusiness" with "Booking page BookingAction" emits a
 *     duplicate Action graph that confuses Search Console).
 *
 * Helpers `buildLocalBusiness`, `buildService`, `buildBookingAction` build
 * the Schema.org graph from the settings/business_info payload + service row.
 */
const JsonLd = ({ type, data, raw, id }) => {
  const seo = useSeoSettings();

  useEffect(() => {
    const tagId = id || `jsonld-${(type || 'misc').toLowerCase()}`;
    let payload = raw;

    if (!payload) {
      switch (type) {
        case 'LocalBusiness':
          payload = buildLocalBusiness(seo);
          break;
        case 'Service':
          payload = buildService(seo, data || {});
          break;
        case 'BookingAction':
          payload = buildBookingAction(seo, data || {});
          break;
        default:
          payload = null;
      }
    }
    if (!payload) return undefined;

    // Remove any prior tag with the same id, then add the fresh one.
    const old = document.getElementById(tagId);
    if (old) old.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = tagId;
    script.text = JSON.stringify(payload);
    document.head.appendChild(script);

    return () => {
      const tag = document.getElementById(tagId);
      if (tag) tag.remove();
    };
  }, [seo, type, data, raw, id]);

  return null;
};

/**
 * Build a Schema.org LocalBusiness graph from the SEO + business_info
 * payload returned by `/api/seo/settings`. The `@type` is read from
 * `seo_business_type` so the admin can choose a more specific subtype
 * (e.g. PetStore, BeautySalon, MedicalBusiness, ProfessionalService) for
 * better Search Console matching.
 */
export const buildLocalBusiness = (s) => {
  if (!s || (!s.business_name && !s.seo_site_title)) return null;
  const type = s.seo_business_type || 'LocalBusiness';
  const node = {
    '@context': 'https://schema.org',
    '@type': type,
    name: s.business_name || s.seo_site_title || 'MorphBook',
  };
  if (s.seo_default_description) node.description = s.seo_default_description;
  if (s.business_logo_url) node.logo = s.business_logo_url;
  if (s.business_logo_url) node.image = s.business_logo_url;
  if (s.business_email) node.email = s.business_email;
  if (s.business_phone) node.telephone = s.business_phone;
  if (s.site_origin || s.business_website_url) {
    node.url = s.business_website_url || s.site_origin;
  }

  // Address — only emit if we have at least a street and city.
  if (s.business_address && s.business_city) {
    node.address = {
      '@type': 'PostalAddress',
      streetAddress: s.business_address,
      addressLocality: s.business_city,
      addressRegion: s.business_state || undefined,
      postalCode: s.business_postal_code || undefined,
      addressCountry: s.business_country || undefined,
    };
  }

  // Geo — only when both lat and lng are provided.
  const lat = parseFloat(s.business_geo_lat);
  const lng = parseFloat(s.business_geo_lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    node.geo = {
      '@type': 'GeoCoordinates',
      latitude: lat,
      longitude: lng,
    };
  }

  // Social profiles → sameAs[]
  const sameAs = [
    s.business_facebook_url,
    s.business_instagram_url,
    s.business_twitter_url,
  ].filter(Boolean);
  if (sameAs.length > 0) node.sameAs = sameAs;

  return node;
};

/**
 * Build a Schema.org Service node. `service` is the raw service row
 * (id, name, description, pricing[]). The Service is offered by the
 * LocalBusiness; we link them via `provider`.
 */
export const buildService = (s, service) => {
  if (!service || !service.name) return null;
  const provider = buildLocalBusiness(s);
  const minPrice = Array.isArray(service.pricing) && service.pricing.length > 0
    ? Math.min(...service.pricing.map((p) => parseFloat(p.price) || 0).filter((n) => n > 0))
    : null;

  const node = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    serviceType: service.name,
  };
  if (service.description) node.description = service.description;
  if (provider) node.provider = provider;
  if (minPrice && minPrice > 0) {
    node.offers = {
      '@type': 'Offer',
      price: minPrice.toFixed(2),
      priceCurrency: s.business_currency || 'USD',
      availability: 'https://schema.org/InStock',
    };
  }
  return node;
};

/**
 * Build a ReserveAction graph for the booking page. This is what tells
 * Google "this page accepts bookings" and may surface a "Reserve" link
 * in Search results in supported verticals.
 */
export const buildBookingAction = (s, ctx) => {
  if (!s || !s.site_origin) return null;
  const target = `${s.site_origin}/book${ctx?.serviceId ? `?service=${encodeURIComponent(ctx.serviceId)}` : ''}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'ReserveAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: target,
      actionPlatform: [
        'http://schema.org/DesktopWebPlatform',
        'http://schema.org/MobileWebPlatform',
      ],
    },
    result: {
      '@type': 'Reservation',
      name: ctx?.serviceName || 'Appointment booking',
    },
  };
};

export default JsonLd;
